import {
  AlertOctagon,
  AlertTriangle,
  Archive,
  CheckCircle,
  Package,
  Wrench,
  XCircle,
} from 'lucide-react';
import type { EquipementCriticite, EquipementStatut } from '@mygmao/shared';

/**
 * Badges réutilisables des statuts/criticités équipement.
 *
 * Mapping (cf. design-system/status-colors.md) :
 *  - en_service       → vert
 *  - en_panne         → rouge
 *  - en_maintenance   → orange
 *  - reforme          → gris
 *  - en_stock         → bleu
 *
 *  - vital            → rouge foncé
 *  - critique         → rouge
 *  - important        → orange
 *  - standard         → gris
 */

export function StatutEquipementBadge({ statut }: { statut: EquipementStatut }) {
  const cfg: Record<EquipementStatut, { cls: string; label: string; Icon: typeof CheckCircle }> = {
    en_service: {
      cls: 'bg-green-100 text-green-800 border-green-200',
      label: 'En service',
      Icon: CheckCircle,
    },
    en_panne: {
      cls: 'bg-red-100 text-red-800 border-red-200',
      label: 'En panne',
      Icon: XCircle,
    },
    en_maintenance: {
      cls: 'bg-orange-100 text-orange-800 border-orange-200',
      label: 'En maintenance',
      Icon: Wrench,
    },
    reforme: {
      cls: 'bg-gray-100 text-gray-700 border-gray-200',
      label: 'Réformé',
      Icon: Archive,
    },
    en_stock: {
      cls: 'bg-blue-100 text-blue-800 border-blue-200',
      label: 'En stock',
      Icon: Package,
    },
  };
  const c = cfg[statut];
  return (
    <span
      className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium border ${c.cls}`}
    >
      <c.Icon className="w-3 h-3 mr-1" />
      {c.label}
    </span>
  );
}

export function CriticiteBadge({ criticite }: { criticite: EquipementCriticite }) {
  const cfg: Record<EquipementCriticite, { cls: string; label: string; Icon: typeof CheckCircle }> =
    {
      vital: {
        cls: 'bg-red-200 text-red-900 border-red-300',
        label: 'Vital',
        Icon: AlertOctagon,
      },
      critique: {
        cls: 'bg-red-100 text-red-700 border-red-200',
        label: 'Critique',
        Icon: AlertTriangle,
      },
      important: {
        cls: 'bg-orange-100 text-orange-800 border-orange-200',
        label: 'Important',
        Icon: AlertTriangle,
      },
      standard: {
        cls: 'bg-gray-100 text-gray-700 border-gray-200',
        label: 'Standard',
        Icon: CheckCircle,
      },
    };
  const c = cfg[criticite];
  return (
    <span
      className={`inline-flex items-center text-xs px-1.5 py-0.5 rounded font-medium border ${c.cls}`}
    >
      <c.Icon className="w-3 h-3 mr-1" />
      {c.label}
    </span>
  );
}
